import { useVirtualizer } from '@tanstack/react-virtual';
import { useState, useMemo, useEffect } from 'react';

interface UseInstaScrollToVirtualThreadEndArgs<E extends HTMLElement> {
  effectDeps?: any[];
  virtualizer: ReturnType<typeof useVirtualizer<E, Element>>;
}

/**
 * React hook that scrolls a virtualized list to its end when specified dependencies change.
 *
 * Triggers a scroll to the last item in the list after the current render cycle whenever any value in {@link effectDeps} changes.
 *
 * @param effectDeps - Optional array of dependencies that, when changed, cause the list to scroll to its end.
 *
 * @remark
 * The scroll action is scheduled in a microtask to ensure it executes after rendering completes.
 */
export function useInstaScrollToVirtualThreadEnd<E extends HTMLElement>({
  virtualizer,
  effectDeps = [],
}: UseInstaScrollToVirtualThreadEndArgs<E>) {
  const [hasScrolled, setHasScrolled] = useState(false);
  const lastIndex = useMemo(() => virtualizer.options.count - 1, [virtualizer.options.count]);

  useEffect(() => {
    if (hasScrolled) return;
    // Use queueMicrotask to ensure the scroll happens after the current render cycle
    queueMicrotask(() => {
      virtualizer.scrollToIndex(lastIndex, { behavior: 'auto', align: 'end' });
    });
  }, [...effectDeps, lastIndex, hasScrolled]);

  useEffect(() => {
    if (!virtualizer.range) return;
    const { endIndex, startIndex } = virtualizer.range;
    const isVisible = lastIndex >= startIndex && lastIndex <= endIndex;
    if (isVisible && !hasScrolled) {
      setHasScrolled(true);
    }
  }, [virtualizer.range, lastIndex, hasScrolled]);
}
